import React from "react";
import {
  useTranslate,
  useLogout,
  useIsExistAuthentication,
  useRouterContext,
  useRouterType,
  useLink,
  useWarnAboutChange,
  useActiveAuthProvider,
  useApiUrl,
} from "@refinedev/core";
import { LogoutOutlined, CloseOutlined } from "@ant-design/icons";
import { Drawer, Menu, theme, Button } from "antd";
import { useLocation } from "react-router-dom";
import { useColorMode } from "../../contexts/color-mode";
import { useTenantLogos, getLogoForMode } from "../../hooks/tenant";
import {
  useMenuTree,
  findSelectedKey,
  findOpenKeys,
  type MenuNode,
} from "./menuItems";

interface DrawerMenuProps {
  open: boolean;
  onClose: () => void;
}

export const DrawerMenu: React.FC<DrawerMenuProps> = ({ open, onClose }) => {
  const { token } = theme.useToken();
  const translate = useTranslate();
  const routerType = useRouterType();
  const NewLink = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const Link = routerType === "legacy" ? LegacyLink : NewLink;
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const isExistAuthentication = useIsExistAuthentication();
  const authProvider = useActiveAuthProvider();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });
  const location = useLocation();
  const menuTree = useMenuTree();
  const selectedKey = findSelectedKey(menuTree, location.pathname);
  const defaultOpenKeys = findOpenKeys(menuTree, selectedKey);
  const apiUrl = useApiUrl();
  const { mode } = useColorMode();
  const { logos: tenantLogos } = useTenantLogos(apiUrl);
  const tenantLogo = getLogoForMode(tenantLogos, mode as "light" | "dark");
  const logoSrc = tenantLogo || `/images/logo-${mode}.png`;

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(
        translate(
          "warnWhenUnsavedChanges",
          "Are you sure you want to leave? You have unsaved changes.",
        ),
      );
      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const renderMenuItems = (nodes: MenuNode[]): React.ReactNode[] => {
    return nodes.map((item) => {
      const { icon, label, route, key, children } = item;
      if (children.length > 0) {
        return (
          <Menu.SubMenu key={key} icon={icon} title={label}>
            {renderMenuItems(children)}
          </Menu.SubMenu>
        );
      }
      return (
        <Menu.Item key={key} icon={icon}>
          <Link to={route ?? ""}>{label}</Link>
        </Menu.Item>
      );
    });
  };

  const menuItems = renderMenuItems(menuTree);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={280}
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
      closable={false}
      styles={{ header: { display: "none" } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 16px",
          height: 56,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          backgroundColor: token.colorBgElevated,
        }}
      >
        <img
          src={logoSrc}
          alt="logo"
          style={{ height: 36, objectFit: "contain" }}
        />
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          size="large"
        />
      </div>
      <Menu
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={defaultOpenKeys}
        mode="inline"
        style={{
          flex: 1,
          border: "none",
          overflow: "auto",
          backgroundColor: token.colorBgElevated,
        }}
        onClick={onClose}
      >
        {menuItems}
      </Menu>
      {isExistAuthentication && (
        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            padding: "8px 0",
          }}
        >
          <Menu
            mode="inline"
            style={{
              border: "none",
              backgroundColor: token.colorBgElevated,
            }}
          >
            <Menu.Item
              key="logout"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              {translate("buttons.logout", "Logout")}
            </Menu.Item>
          </Menu>
        </div>
      )}
    </Drawer>
  );
};
